import isEmpty from 'lodash.isempty';
import get from 'lodash.get';
import isNil from 'lodash.isnil';
import { compareDesc, parseISO } from 'date-fns';
import ScoreInterpretationMapper from '../../constants/shared/ScoreInterpretationMapper';
import { TRC_SCORE } from '../../constants/shared/TrcConstants';
import { MEASURE } from '../../constants/shared/MeasuresConstants';

const { PC, RB } = TRC_SCORE;
const { TRC } = MEASURE;
const NOT_AVAILABLE_SORT_INDEX = -6;
const DASH_SORT_INDEX = -5;
const PAUSED_SORT_INDEX = -4;
const DISCONTINUED_SORT_INDEX = -2;
const GREATER_THAN_SYMBOL = '>';
const LESS_THAN_SYMBOL = '<';

/**
 * Sort students by name
 * @param {Object} first student data object
 * @param {Object} second student data object
 * @returns {Number} returns a number indicating whether the string comes before,
 * after or is equal as the compareString in sort order
 */
export const sortStudentsByName = (studentRow1, studentRow2) => {
    const studentName1 = studentRow1.studentInfo.lastName + studentRow1.studentInfo.firstName;
    const studentName2 = studentRow2.studentInfo.lastName + studentRow2.studentInfo.firstName;
    return studentName1.localeCompare(studentName2);
};

/**
 * Sort list of items by provided key using pre-defined order
 * @param {Array} items list of items to sort
 * @param {Array} predefinedList list with pre-defined order for sorting
 * @param {string} sortKey is optional
 * @returns {Array} list of sorted items
 */
export const sortByPredefinedOrder = (items, predefinedList, sortKey) =>
    Array.from(items).sort((item1, item2) => {
        const firstItemIndex = predefinedList.indexOf(get(item1, sortKey, item1));
        const secondItemIndex = predefinedList.indexOf(get(item2, sortKey, item2));
        return firstItemIndex - secondItemIndex;
    });

/**
 * Apply sorting to passed in objects based on iterpretation sort index.
 * In case of equal interpretations use score sort index.
 */
const applyMeasureSorting = (firstRowSortIndex, secondRowSortIndex) => {
    let measureSorting = 0;
    if (!isEmpty(firstRowSortIndex) && !isEmpty(secondRowSortIndex)) {
        measureSorting = firstRowSortIndex.interpSortIndex - secondRowSortIndex.interpSortIndex;

        if (measureSorting === 0) {
            return firstRowSortIndex.scoreSortIndex - secondRowSortIndex.scoreSortIndex;
        }
    }

    return measureSorting;
};

/**
 * Apply sorting to passed in objects based on `lastName` and `firstName` values from those objects.
 */
const applyStudentNameSorting = (firstRowStudName, secondRowStudName) => {
    const firstStudentLastName = firstRowStudName?.studentInfo?.lastName;
    const secondStudentLastName = secondRowStudName?.studentInfo?.lastName;
    let studentNameSorting;

    if (firstStudentLastName && secondStudentLastName) {
        studentNameSorting = firstStudentLastName.localeCompare(secondStudentLastName);
    }

    if (!studentNameSorting)
        studentNameSorting = firstRowStudName?.studentInfo?.firstName.localeCompare(
            secondRowStudName?.studentInfo?.firstName,
        );

    return studentNameSorting;
};

/**
 * Sorting logic based on measure sorting as primary and then student name as secondary sorting
 * In case if primarySorting doesn't help to figure out the order of passed in objects
 * - sorting based on `lastName` and `firstName` is applied
 */
export const benchmarkSorting = (
    subheaderKey,
    firstRowSortIndex,
    secondRowSortIndex,
    firstRowStudName,
    secondRowStudName,
    measures,
) => {
    let primarySorting = 0;
    if (measures.some(measure => subheaderKey.includes(measure.key))) {
        primarySorting = applyMeasureSorting(firstRowSortIndex, secondRowSortIndex);
    }

    return primarySorting !== 0
        ? primarySorting
        : applyStudentNameSorting(firstRowStudName, secondRowStudName);
};

/**
 * Specifc logic for calculating sorting index for `TRC` assmnt
 */
export const getTrcScoreSortIndex = (trcScore, isComplete) => {
    let sortedScore = `${get(trcScore, 'rawValue', '')}`;
    const isOver = sortedScore.includes(GREATER_THAN_SYMBOL);
    const isUnder = sortedScore.includes(LESS_THAN_SYMBOL);
    if (isOver || isUnder) {
        sortedScore = sortedScore.replace(GREATER_THAN_SYMBOL, '').replace(LESS_THAN_SYMBOL, '');
    }
    let trcSortIndex;
    if (!isComplete || isEmpty(sortedScore)) {
        trcSortIndex = NOT_AVAILABLE_SORT_INDEX;
    } else if (sortedScore === PC) {
        trcSortIndex = DASH_SORT_INDEX;
    } else if (sortedScore === RB) {
        trcSortIndex = PAUSED_SORT_INDEX;
    } else if (/^\d+$/.test(sortedScore)) {
        trcSortIndex = +sortedScore;
    } else {
        trcSortIndex = sortedScore.charCodeAt(0);
    }

    let adjustedSortIndex = trcSortIndex * 2;
    if (isOver) {
        adjustedSortIndex += 1;
    } else if (isUnder) {
        adjustedSortIndex -= 1;
    }

    return adjustedSortIndex;
};

/**
 * Calculating sorting index based on `interpretation` value
 */
export const getInterpSortIndex = (
    measure,
    statusForMeasureYearPeriod,
    isMeasureAvailable,
    measureConst,
    gatingType,
) => {
    let interpSortIndex = NOT_AVAILABLE_SORT_INDEX;

    if (!isEmpty(statusForMeasureYearPeriod)) {
        const { score, isComplete, interp } = statusForMeasureYearPeriod;

        if (!isNil(score) && isMeasureAvailable) {
            interpSortIndex = DASH_SORT_INDEX;
        }

        if (measureConst.includes(measure) && !isComplete) {
            interpSortIndex = PAUSED_SORT_INDEX;
        }

        if (!isNil(gatingType) && isComplete) {
            interpSortIndex = DISCONTINUED_SORT_INDEX;
        }

        if (interp && isNil(gatingType)) {
            const interpKeys = Object.keys(ScoreInterpretationMapper);
            interpSortIndex = interpKeys.indexOf(interp);
        }
    }

    return interpSortIndex;
};

/**
 * Calculating sorting index based on `score` value
 */
export const getScoreSortIndex = (measure, statusForMeasureYearPeriod) => {
    const score = get(statusForMeasureYearPeriod, 'score', '');
    const isComplete = get(statusForMeasureYearPeriod, 'isComplete', false);
    return measure === TRC ? getTrcScoreSortIndex(score, isComplete) : score;
};

/**
 *
 * @param {Array} results(Required) Array of objects.
 * @param {String} key(Optional) Path to `date` property in object. ( Default value: `'date'` ).
 * @returns Array sorted by date value.
 */
export const getSortedResultsByDate = (results = [], key = 'date') =>
    Array.from(results).sort((result1, result2) => {
        const firstResultDate = parseISO(result1[key]);
        const secondResultDate = parseISO(result2[key]);
        return compareDesc(firstResultDate, secondResultDate);
    });
